<!-- the component implementation is as follows:

<Button>
  <LeftArrowIcon />
  Click here
  <RightArrowIcon />
</Button> -->

<script lang="ts">
  import { loadingAction } from 'svelte-legos';

  export let href: string | null = null;
  export let type: 'button' | 'reset' | 'submit' = 'button';
  export let disabled = false;
  export let variant:
    | 'priority'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructive'
    | 'ai' = 'primary';
  export let size:
    | 'xsmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'xxlarge' = 'large';
  export { className as class };
  export let outline = false;
  export let expand = false;
  export let fullWidth = false;
  export let loading = false;

  let className = '';

  $: classes = `${variant} ${size} ${outline ? 'outline' : ''} ${
    disabled ? 'disabled' : ''
  } ${expand ? 'expand' : ''} ${fullWidth ? 'full-width' : ''} ${className}`;
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element
  this={href ? 'a' : 'button'}
  {href}
  type={href != null ? null : type}
  disabled={href != null ? null : disabled}
  class={classes}
  use:loadingAction={loading}
  on:click
>
  <slot>Click here</slot>
  {#if variant === 'ai'}
    <span class="ai">AI</span>
  {/if}
</svelte:element>

<style lang="scss">
  button,
  a {
    text-decoration: none;
    display: inline-flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    border-radius: var(--spacing-atom);
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;

    &.expand {
      width: 100%;
      max-width: 358px;
    }

    &.full-width {
      width: 100%;
    }

    @media screen and (max-width: 768px) {
      &.expand {
        max-width: unset;
      }
    }

    /* colors/variants */
    &.priority {
      background-color: var(--vlt-lp-accent, var(--color-accent));
      color: var(--vlt-lp-accent-text, var(--color-neutralBlack));
    }

    &.primary {
      background-color: var(--vlt-lp-primary, var(--color-primary));
      color: var(--vlt-lp-primary-text, var(--color-neutralWhite));
    }

    &.secondary {
      background-color: var(--vlt-lp-secondary, var(--color-primary-light));
      color: var(--vlt-lp-secondary-text, var(--color-primary));

      &:hover {
        box-shadow:
          0px 2px 4px 0px rgba(17, 17, 17, 0.04),
          0px 1px 2px 0px rgba(0, 0, 0, 0.02);
      }
    }

    &.tertiary {
      background-color: transparent;
      color: var(--color-neutralBlack);

      &:hover {
        background-color: var(--color-neutralExtraLight);
      }
    }

    &.destructive {
      background-color: var(--color-neutralWhite);
      color: var(--color-error);

      &:hover {
        background-color: var(--color-error-light);
        box-shadow:
          0px 2px 4px 0px rgba(17, 17, 17, 0.04),
          0px 1px 2px 0px rgba(0, 0, 0, 0.02);
      }
    }

    &.ai {
      background: linear-gradient(90deg, #058beb 0%, #9747ff 67.75%);
      color: var(--color-neutralWhite);
      border: none;
      box-shadow:
        0 0 0 1px var(--color-neutralWhite),
        0 0 0 2px var(--color-primary);

      .ai {
        padding: var(--spacing-quarck) var(--spacing-nano);
        background-color: var(--color-neutralWhite);
        color: var(--color-primary);
        border-radius: 2px;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0.06px;
      }
    }

    // outline
    &.priority,
    &.primary,
    &.secondary,
    &.tertiary,
    &.destructive {
      outline: none;
      border: none;
    }

    &.primary.outline {
      border: 1px solid var(--color-primary);
      color: var(--color-primary);
      background: var(--color-neutralWhite);

      &:hover {
        background-color: var(--color-primary-light);
      }
    }

    &.secondary.outline {
      border: 1px solid var(--color-neutralExtraLight);
      background: var(--color-neutralWhite);
      &:hover {
        background-color: var(--color-primary-light);
      }
    }

    &.tertiary.outline {
      border: 1px solid var(--color-neutralExtraLight);
    }

    &.destructive.outline {
      border: 2px solid var(--color-error-light);
    }

    /* sizes */
    &.xxlarge,
    &.xlarge,
    &.large,
    &.medium {
      :global(> svg) {
        width: var(--spacing-xsmall);
        height: var(--spacing-xsmall);
      }
    }

    &.xsmall,
    &.small {
      :global(> svg) {
        width: var(--spacing-micro);
        height: var(--spacing-micro);
      }
    }

    &.xxlarge {
      font-size: var(--base-l-regular);
      line-height: 24px;
      padding: var(--spacing-mini);
      gap: var(--spacing-micro);
      height: 56px;
    }

    &.xlarge {
      font-size: var(--base-l-regular);
      line-height: 24px;
      padding: var(--spacing-micro) var(--spacing-mini);
      gap: var(--spacing-micro);
      height: 48px;
    }

    &.large {
      font-size: var(--base-l-regular);
      line-height: 24px;
      padding: var(--spacing-nano) var(--spacing-micro);
      gap: var(--spacing-micro);
      height: 40px;
    }

    &.medium {
      font-size: var(--base-m-regular);
      line-height: 20px;
      padding: var(--spacing-nano) var(--spacing-micro);
      gap: var(--spacing-nano);
      height: 32px;
    }

    &.small {
      font-size: var(--base-s-regular);
      line-height: 18px;
      padding: var(--spacing-atom);
      gap: var(--spacing-nano);
      height: 26px;
    }

    &.xsmall {
      font-size: var(--base-s-regular);
      line-height: 18px;
      padding: var(--spacing-quarck) var(--spacing-atom);
      gap: var(--spacing-nano);
      height: 22px;
    }

    &.disabled {
      cursor: not-allowed;
      color: var(--color-neutralExtraLight) !important;
      background-color: var(--color-background) !important;
      &:hover {
        box-shadow: unset;
      }
      :global(> svg path) {
        stroke: var(--color-neutralExtraLight) !important;
        fill: var(--color-background) !important;
      }
      &.tertiary.outline,
      &.secondary.outline,
      &.destructive.outline {
        border: 1px solid var(--color-neutralExtraLight);
      }
    }
  }
</style>
